/**
 * Robust helper to extract user-friendly error messages from backend error responses.
 *
 * @param {object} err           - Error object (AxiosError or StatusCodeError)
 * @param {string} defaultMessage - Default message if extraction fails
 * @returns {string}
 */
export const extractErrorMessage = (err, defaultMessage = 'An error occurred') => {
	const data = err?.error || err?.response?.data;
	if (data) {
		if (typeof data === 'string') {
			return data;
		}

		const getMsgFromPayload = (obj) => {
			if (!obj) return null;
			if (typeof obj === 'string') return obj;

			if (obj.error && typeof obj.error === 'object') {
				if (obj.error.message) return obj.error.message;
				if (Array.isArray(obj.error.errors) && obj.error.errors.length > 0) {
					return obj.error.errors[0];
				}
			}

			if (obj.message && typeof obj.message === 'string' && !obj.message.includes('{')) {
				return obj.message;
			}
			if (Array.isArray(obj.errors) && obj.errors.length > 0) {
				return typeof obj.errors[0] === 'string' ? obj.errors[0] : getMsgFromPayload(obj.errors[0]);
			}

			return null;
		};

		if (data.error) {
			const nestedMsg = getMsgFromPayload(data.error);
			if (nestedMsg) return nestedMsg;
		}

		if (data.message && typeof data.message === 'string') {
			if (data.message.includes('{')) {
				const jsonStartIdx = data.message.indexOf('{');
				try {
					const jsonStr = data.message.substring(jsonStartIdx);
					const parsed = JSON.parse(jsonStr);
					const nestedMsg = getMsgFromPayload(parsed);
					if (nestedMsg) return nestedMsg;
				} catch (e) {
					// Ignore
				}
			} else {
				return data.message;
			}
		}
	}

	// Fallback to parse JSON from err.message if present
	if (typeof err?.message === 'string') {
		const jsonStartIdx = err.message.indexOf('{');
		if (jsonStartIdx !== -1) {
			try {
				const jsonStr = err.message.substring(jsonStartIdx);
				const parsed = JSON.parse(jsonStr);
				if (parsed) {
					if (parsed.error && typeof parsed.error === 'object' && parsed.error.message) {
						return parsed.error.message;
					}
					if (parsed.message && typeof parsed.message === 'string' && !parsed.message.includes('{')) {
						return parsed.message;
					}
					if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
						return parsed.errors[0];
					}
				}
			} catch (e) {
				// Ignore
			}
		}
		return err.message;
	}

	return defaultMessage;
};
